/**
 * One-time callout for the v3.0 -> v3.1 correction (2026-09-17).
 *
 * An earlier version of the Master Agent Participation Agreement was
 * distributed in error and has since been replaced. The replacement corrects
 * the compensation terms in Section 19 (Program-Specific Compensation Terms),
 * specifically relating to Share360. Everyone who reaches this screen is
 * about to sign v3.1 regardless, so this exists purely to make sure they
 * notice what changed rather than treating the resigning prompt as a routine
 * re-click.
 *
 * Deliberately NOT wired to AGREEMENT_VERSION or any other automatic
 * condition: this is a one-off notice about one specific correction, decided
 * by a human, not a general "the agreement changed" mechanism. When this
 * revision is no longer news (the client should decide when, not a version
 * number), delete this component and its two call sites in JoinAgent.tsx and
 * RepDashboard.tsx rather than trying to make it conditionally disappear.
 */
import { AlertTriangle } from "lucide-react";

export default function AgreementRevisionNotice({ theme = "dark" }: { theme?: "dark" | "light" }) {
  const dark = theme === "dark";
  const shell = dark ? "bg-amber-500/10 border-amber-500/25" : "bg-amber-50 border-amber-200";
  const heading = dark ? "text-amber-300" : "text-amber-800";
  const body = dark ? "text-amber-100/80" : "text-amber-900/80";

  return (
    <div className={`rounded-xl border p-4 flex items-start gap-3 ${shell}`}>
      <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${heading}`} />
      <div>
        <div className={`text-sm font-semibold mb-1 ${heading}`}>This agreement has been updated</div>
        <p className={`text-xs leading-relaxed ${body}`}>
          Please note that this Master Agent Participation Agreement replaces an earlier version that was
          distributed in error. A revision was made to the compensation terms in Section 19
          (Program-Specific Compensation Terms), specifically relating to Share360. We ask that you read the
          agreement in full, with particular attention to that section, before signing.
        </p>
      </div>
    </div>
  );
}
